import { useLazyQuery, useMutation } from '@apollo/client';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Form, Input, Upload } from 'antd';
import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { REQUEST_TYPES } from '../../common/constants';
import {
  fileUpload,
  formValidatorRules,
  getBase64,
  validateImage,
  validateVideo,
} from '../../common/utils';
import CommonPreview from '../CommonPreview';
import ModalComponent from '../ModalComponent';
import { MARK_INVENTORY_UNIT_DAMAGED } from './graphql/Mutations';
import {
  GET_DAMAGE_REQUEST_IMAGE_SIGNED_PUT_URL,
  GET_DAMAGE_REQUEST_VIDEO_SIGNED_PUT_URL,
} from './graphql/Queries';
import {
  buildChecklistAnswersInput,
  buildClientRequestId,
  buildPiecesInput,
} from './utils';

const { Item } = Form;
const { TextArea } = Input;
const { whitespaceNotAllowed } = formValidatorRules;

const MAX_IMAGES = 4;

const buildFileKey = (file, uuid) => {
  const { name } = file || {};
  const ext = name?.substring(name?.lastIndexOf('.') + 1);
  const filename = name?.split('.')?.slice(0, -1)?.join('.');
  return `toy/${uuid}/${Date?.now()}_${filename}.${ext}`;
};

const UnitDamageModal = ({
  open,
  onClose,
  onDamaged,
  onDamageDetails,
  preview,
  unit,
  orderItemId,
  inspectionForm,
  pieceCounts,
  checklistAnswers,
  notes,
}) => {
  const [form] = Form.useForm();
  const [images, setImages] = useState([]);
  const [video, setVideo] = useState([]);
  const [previewImage, setPreviewImage] = useState();
  const [previewVideo, setPreviewVideo] = useState();
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [getImageSignedUrl] = useLazyQuery(
    GET_DAMAGE_REQUEST_IMAGE_SIGNED_PUT_URL,
    {
      fetchPolicy: 'network-only',
      onError() {},
    },
  );
  const [getVideoSignedUrl] = useLazyQuery(
    GET_DAMAGE_REQUEST_VIDEO_SIGNED_PUT_URL,
    {
      fetchPolicy: 'network-only',
      onError() {},
    },
  );
  const [markUnitDamaged] = useMutation(MARK_INVENTORY_UNIT_DAMAGED, {
    errorPolicy: 'all',
    onError() {},
  });

  const isAttachmentRequired = Boolean(preview?.requiresAttachment);
  const isDetailsOnly = Boolean(onDamageDetails);

  const handleClose = () => {
    form?.resetFields();
    setImages([]);
    setVideo([]);
    setPreviewVideo();
    setError('');
    onClose();
  };

  const handleImageChange = (info) => {
    validateImage(info, form, setImages, 'images');
  };

  const handleVideoChange = async (info) => {
    const isValid = await validateVideo(info, form, setVideo, 'video');
    if (isValid) {
      // eslint-disable-next-line no-undef
      setPreviewVideo(URL.createObjectURL(info?.fileList?.[0]?.originFileObj));
    }
  };

  const handleImagePreview = async (file) => {
    const preview64 = file?.url ? null : await getBase64(file?.originFileObj);
    setPreviewImage(file?.url || preview64);
    setIsPreviewVisible(true);
  };

  const uploadImages = async (uuid, files) => {
    if (!files?.length) return undefined;
    const response = await getImageSignedUrl({
      variables: {
        data: {
          fileNames: files?.map((file) => buildFileKey(file, uuid)),
          type: REQUEST_TYPES?.LIBRARY_DAMAGE,
        },
      },
    });
    const signedUrls =
      response?.data?.getLibraryDamageRequestImageSignedPutUrl || [];
    return Promise.all(
      signedUrls?.map(async (signedData, index) => {
        await fileUpload(signedData?.signedUrl, files?.[index]?.originFileObj);
        return { key: signedData?.fileName };
      }),
    );
  };

  const uploadVideo = async (uuid, file) => {
    if (!file) return undefined;
    const response = await getVideoSignedUrl({
      variables: { data: { fileName: buildFileKey(file, uuid) } },
    });
    const signedData = response?.data?.getLibraryRequestToyVideoSignedPutUrl;
    if (!signedData?.signedUrl) return undefined;
    await fileUpload(signedData?.signedUrl, file?.originFileObj);
    return signedData?.fileName;
  };

  const handleFinish = async (values) => {
    setError('');
    setIsSubmitting(true);
    const uuid = uuidv4();
    try {
      const [damageImages, videoKey] = await Promise.all([
        uploadImages(uuid, values?.images),
        uploadVideo(uuid, values?.video?.[0]),
      ]);

      if (isDetailsOnly) {
        onDamageDetails({
          damageDescription: values?.description?.trim(),
          damageImages,
          videoKey,
        });
        handleClose();
        return;
      }

      const response = await markUnitDamaged({
        variables: {
          data: {
            qrCode: unit?.qrCode,
            unitCode: unit?.unitCode,
            description: values?.description?.trim(),
            images: damageImages,
            videoKey,
            imageUuid: uuid,
            pieces: buildPiecesInput(
              inspectionForm?.requiredContents,
              pieceCounts,
            ),
            checklist: buildChecklistAnswersInput(
              inspectionForm?.checklist,
              checklistAnswers,
            ),
            notes: notes?.trim() || undefined,
            orderItemId,
            clientRequestId: buildClientRequestId('damage', unit?.qrCode),
          },
        },
      });

      const data = response?.data?.markInventoryUnitDamaged;
      if (data?.data?.unitId) {
        onDamaged(
          data?.message ||
            'This unit is marked damaged and a damage request has been raised.',
        );
        handleClose();
        return;
      }
      setError(
        response?.errors?.[0]?.message ||
          'Could not mark this unit damaged. Please try again.',
      );
    } catch (uploadError) {
      setError(uploadError?.message || 'Upload failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <CommonPreview
        visible={isPreviewVisible}
        setVisible={setIsPreviewVisible}
        image={previewImage}
        setImage={setPreviewImage}
      />
      <ModalComponent
        title="Damage waiver request"
        okText="Submit"
        cancelText="Cancel"
        open={open}
        onOk={form?.submit}
        onCancel={handleClose}
        confirmLoading={isSubmitting}
        className="modal unit-damage-modal"
        destroyOnClose
      >
        <Alert
          className="mb-16"
          type="warning"
          showIcon
          message="This unit did not pass its inspection"
          description={
            preview?.message ||
            'Raise a damage request to record what is wrong with it.'
          }
        />
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Item
            name="description"
            label="Damage Description"
            rules={[
              { required: true, message: 'Please Enter Damage Description!' },
              whitespaceNotAllowed,
            ]}
          >
            <TextArea
              rows={3}
              allowClear
              showCount
              maxLength={700}
              placeholder="What is damaged or missing?"
            />
          </Item>
          <Item
            name="images"
            label="Upload Images"
            rules={[
              {
                required: isAttachmentRequired,
                message: 'Please Upload Images!',
              },
            ]}
          >
            <Upload
              listType="picture-card"
              maxCount={MAX_IMAGES}
              fileList={images}
              beforeUpload={() => false}
              onPreview={handleImagePreview}
              onChange={handleImageChange}
              className="avatar-uploader"
              showUploadList={{
                showDownloadIcon: false,
                showPreviewIcon: true,
                showRemoveIcon: true,
              }}
            >
              {images?.length !== MAX_IMAGES && <PlusOutlined />}
            </Upload>
          </Item>
          <Item name="video" label="Video">
            {!previewVideo ? (
              <Upload
                listType="picture-card"
                maxCount={1}
                fileList={video}
                beforeUpload={() => false}
                onChange={handleVideoChange}
                showUploadList={{
                  showDownloadIcon: false,
                  showPreviewIcon: false,
                  showRemoveIcon: false,
                }}
              >
                {video?.length !== 1 && <PlusOutlined />}
              </Upload>
            ) : (
              <div className="toy-video-container mb-10">
                <video
                  width="100%"
                  height="100%"
                  controls
                  src={previewVideo}
                  className="toy-video-upload"
                >
                  <track kind="captions" />
                </video>
                <DeleteOutlined
                  className="toy-video-delete-icon"
                  onClick={() => {
                    setVideo([]);
                    setPreviewVideo();
                  }}
                />
              </div>
            )}
          </Item>
          {error && <Alert type="error" showIcon message={error} />}
        </Form>
      </ModalComponent>
    </>
  );
};

export default UnitDamageModal;
